import { useState, useEffect } from "react";

export default function FloatingButtons() {
  const [isVisible, setIsVisible] = useState(false);
  const [ratingData, setRatingData] = useState({ rating: "9.4", reviews: "1,150", word: "Superb" });

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener("scroll", toggleVisibility);
    
    // Fetch dynamic rating dari API/Domain hasil scrape (diatur via environment variable)
    const apiUrl = import.meta.env.VITE_BOOKING_RATING_API || '/rating.json';
    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        if (data.rating && data.reviews) {
          setRatingData(data);
        }
      })
      .catch(err => console.error("Failed to fetch dynamic rating data", err));

    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <div className="fixed bottom-6 right-6 z-[30] flex flex-col gap-3">
      {/* Styles for pulse effect */}
      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(0.95); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .whatsapp-pulse {
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background-color: #25D366;
          opacity: 0.6;
          z-index: -1;
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .booking-pulse {
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background-color: #003B95;
          opacity: 0.6;
          z-index: -1;
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          animation-delay: 1s;
        }
      `}</style>

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`w-12 h-12 rounded-full bg-black/80 text-white border border-white/10 hover:border-gold hover:text-gold shadow-2xl flex items-center justify-center cursor-pointer transition-all duration-300 transform ${
          isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-75 pointer-events-none"
        }`}
        aria-label="Back to top"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      </button>

      {/* Booking.com Button */}
      <div className="relative flex items-center justify-end group">
        <div className="absolute right-14 mr-2 bg-white text-[#003B95] px-3 py-1.5 rounded-lg shadow-xl border border-gray-100 flex flex-col items-center whitespace-nowrap">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="bg-[#003B95] text-white text-[11px] px-1.5 py-0.5 rounded-sm font-bold shadow-sm">{ratingData.rating}</span>
            <span className="text-xs font-bold text-gray-800">{ratingData.word}</span>
          </div>
          <span className="text-[10px] text-gray-500">{ratingData.reviews} reviews</span>
          {/* Arrow */}
          <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 bg-white transform rotate-45 border-r border-t border-gray-100"></div>
        </div>
        <a
          href="https://www.booking.com/hotel/id/patal-kikian-sidemen.html"
          target="_blank"
          rel="noopener noreferrer"
          className="relative w-12 h-12 rounded-full bg-[#003B95] text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform duration-300 border border-white/10"
          aria-label="Book on Booking.com"
        >
          <span className="booking-pulse"></span>
          <span className="font-bold text-xl font-sans tracking-tighter">B.</span>
        </a>
      </div>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/6281353278504"
        target="_blank"
        rel="noopener noreferrer"
        className="relative w-12 h-12 rounded-full bg-[#25D366] text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform duration-300"
        aria-label="Chat on WhatsApp"
      >
        <span className="whatsapp-pulse"></span>
        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.005 5.277 5.282 0 11.768 0c3.141.001 6.096 1.224 8.322 3.452 2.228 2.228 3.45 5.184 3.448 8.325-.006 6.485-5.283 11.76-11.77 11.76-1.996-.001-3.957-.509-5.696-1.479L0 24zm6.49-3.229c1.677.995 3.325 1.52 5.275 1.521 5.373 0 9.742-4.368 9.747-9.743.002-2.604-1.013-5.05-2.86-6.898C16.815 3.805 14.37 2.79 11.769 2.79c-5.377 0-9.748 4.369-9.753 9.745-.001 1.894.493 3.748 1.431 5.388L2.433 21.57l3.87-1.013-.244.214zM16.57 14.86c-.267-.134-1.58-.78-1.822-.868-.243-.088-.419-.133-.596.134-.176.265-.68.868-.834 1.045-.155.176-.309.198-.576.064-.267-.133-1.127-.416-2.148-1.328-.794-.708-1.33-1.582-1.486-1.848-.155-.266-.017-.41.117-.542.12-.119.267-.31.401-.465.135-.155.179-.265.269-.442.09-.176.044-.332-.022-.465-.067-.133-.596-1.437-.817-1.97-.215-.518-.468-.448-.596-.454-.12-.006-.282-.007-.441-.007-.16 0-.419.06-.639.299-.219.239-.838.82-.838 2.001 0 1.18.86 2.319.98 2.48.12.162 1.693 2.585 4.1 3.62.573.247 1.02.394 1.368.504.576.182 1.099.156 1.513.094.461-.069 1.42-.58 1.62-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.488-.294z" />
        </svg>
      </a>
    </div>
  );
}
